'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DownloadFile, DownloadRecordType } from '@velocity-exchange/common';
import { VELOCITY_PROGRAM_ID } from '@velocity-exchange/sdk';
import Env from 'src/environmentVariables/EnvironmentVariables';
import useApiAuth, { type ApiAuthHeaders } from 'src/hooks/useApiAuth';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useExportsSessionStore from 'src/stores/useExportsSessionStore';
import { createQueryKey } from 'src/utils/query/createQueryKey';

const SECONDS_PER_DAY = 24 * 60 * 60;
const POLL_INTERVAL_MS = 20_000;
const MAX_PAGES = 50; // safety cap on cursor pagination (20 records/page)

/**
 * Convert a YYYYMMDD S3 date string to the unix-seconds timestamp of that day's
 * UTC midnight. Deliberately UTC (not `s3DateStringToDate`, which is local): the
 * exported range is a calendar-day window, so "20260101" must mean the Jan 1 UTC
 * day for every user, not a range shifted by their local UTC offset.
 */
function s3DateStringToUtcSeconds(dateStr: string): number {
	const year = Number(dateStr.slice(0, 4));
	const month = Number(dateStr.slice(4, 6)) - 1;
	const day = Number(dateStr.slice(6, 8));
	return Math.floor(Date.UTC(year, month, day) / 1000);
}

/**
 * Inverse of {@link s3DateStringToUtcSeconds}: unix seconds → YYYYMMDD in UTC, so
 * the day rendered in the table round-trips the day that was requested/returned
 * regardless of the viewer's timezone.
 */
function utcSecondsToS3DateString(seconds: number): string {
	const d = new Date(seconds * 1000);
	const year = d.getUTCFullYear();
	const month = d.getUTCMonth() + 1;
	const day = d.getUTCDate();
	return `${year}${month < 10 ? '0' + month : month}${
		day < 10 ? '0' + day : day
	}`;
}

// Action embedded in the signed message for the POST endpoint. Must match the
// `SUBMIT_ACTION` the aggregator-api expects, otherwise the request 401s.
export const REQUEST_EXPORT_ACTION = 'requestExport';

/** A single export job as returned by GET /authority/{authority}/exports. */
interface ExportJobRecord {
	requestId: string;
	status: string;
	fileType: DownloadRecordType;
	/** Unix seconds. */
	from: number;
	/** Unix seconds. */
	to: number;
	userPublicKeys: string[];
	market: string | null;
	/** Unix seconds. */
	createdAt: number;
	/** Unix seconds. */
	completedAt: number | null;
	recordCount: number | null;
	errorReason: string | null;
	/** Presigned S3 URL, present only once the job is ready. */
	downloadUrl: string | null;
}

interface ExportsListResponse {
	success: boolean;
	records: ExportJobRecord[];
	meta: { nextPage: string | null };
}

interface RequestExportResponse {
	success: boolean;
	requestId: string;
	status: string;
}

export interface RequestExportParams {
	fileType: DownloadRecordType;
	/** S3 date string (YYYYMMDD) for the start of the range, inclusive. */
	fromS3Date: string;
	/** S3 date string (YYYYMMDD) for the end of the range, inclusive. */
	toS3Date: string;
	userPublicKeys: string[];
	marketSymbol?: string;
}

/** Error carrying the HTTP status so callers can tailor messaging (e.g. 429). */
export class ExportRequestError extends Error {
	constructor(
		public readonly status: number,
		message: string
	) {
		super(message);
		this.name = 'ExportRequestError';
	}
}

function getExportsQueryKey(authority: string | undefined) {
	return createQueryKey('exports', authority);
}

function exportsPath(authority: string) {
	return `${Env.dataApiUrl}/authority/${authority}/exports`;
}

/**
 * Walk the cursor-paginated GET endpoint until there are no more pages, so the
 * exports table and pending-download detection see the full history (matching
 * the old "fetch everything" behaviour of the history server).
 */
async function fetchAllExportJobs(
	authority: string,
	headers: ApiAuthHeaders
): Promise<ExportJobRecord[]> {
	const records: ExportJobRecord[] = [];
	let page: string | undefined;
	let pageCount = 0;

	do {
		const query = page ? `?page=${encodeURIComponent(page)}` : '';
		const response = await fetch(`${exportsPath(authority)}${query}`, {
			headers: { ...headers },
		});

		if (!response.ok) {
			const errorText = await response.text().catch(() => '');
			throw new ExportRequestError(
				response.status,
				`Exports API error: ${response.status} - ${errorText}`
			);
		}

		const body = (await response.json()) as ExportsListResponse;
		records.push(...(body.records ?? []));
		page = body.meta?.nextPage ?? undefined;
		pageCount += 1;
	} while (page && pageCount < MAX_PAGES);

	// Truncating (rather than throwing) keeps the table usable if a user ever has
	// > MAX_PAGES * 20 jobs; surface it in dev so the cap can be revisited.
	if (page) {
		console.warn(
			`[useExportRequests] hit MAX_PAGES (${MAX_PAGES}) with more pages remaining; export history is truncated.`
		);
	}

	return records;
}

/**
 * `DownloadFile` augmented with export-job terminal state, so polling can stop
 * on jobs that will never produce a URL (failed/completed-empty) instead of
 * treating every missing `downloadUrl` as still-pending.
 */
export interface ExportDownloadFile extends DownloadFile {
	/** Job is still running: no URL yet, and not in a terminal state. */
	isPendingExport: boolean;
	/** Set when the job terminated with an error, so the row can show "Failed". */
	errorReason: string | null;
}

/**
 * Map an export job into the legacy `DownloadFile` shape the exports table and
 * mobile card render against. `from`/`to`/`createdAt` are unix seconds; the
 * table works in S3 date strings, so convert here.
 */
function toDownloadFile(
	record: ExportJobRecord,
	authority: string
): ExportDownloadFile {
	return {
		key: record.requestId,
		requestParams: {
			fileType: record.fileType,
			requestedDate: utcSecondsToS3DateString(record.createdAt),
			fromDate: utcSecondsToS3DateString(record.from),
			toDate: utcSecondsToS3DateString(record.to),
			user: record.userPublicKeys[0] ?? '',
			programId: VELOCITY_PROGRAM_ID,
			authority,
			isEmulationMode: false,
			marketSymbol: record.market ?? undefined,
		},
		lastModifiedTs: (record.completedAt ?? record.createdAt) * 1000,
		downloadUrl: record.downloadUrl ?? undefined,
		isPendingExport:
			!record.downloadUrl &&
			record.completedAt === null &&
			record.errorReason === null,
		errorReason: record.errorReason,
	};
}

/**
 * Fetches the connected authority's export jobs. Polls every 20s while any job
 * is still pending (no download URL yet), then stops.
 *
 * The endpoint is signature-gated to the connected wallet, so fetching prompts
 * a wallet signature and cannot serve emulated authorities. Pass `enabled:
 * false` to keep the query dormant (no fetch, no signature) until the data is
 * actually needed — see `useDownloadRequests`, which only enables it once the
 * user has requested an export.
 */
export function useExportRequests({
	enabled = true,
}: { enabled?: boolean } = {}) {
	const authority = useVelocityStore(
		(s) => s.wallet.current.adapter?.publicKey
	);
	const authorityStr = authority?.toBase58();
	const { getAuthHeaders, canSign } = useApiAuth();

	return useQuery({
		queryKey: getExportsQueryKey(authorityStr),
		queryFn: async () => {
			if (!authorityStr) return [];
			const headers = await getAuthHeaders();
			const jobs = await fetchAllExportJobs(authorityStr, headers);
			return jobs
				.map((job) => toDownloadFile(job, authorityStr))
				.sort((a, b) => b.lastModifiedTs - a.lastModifiedTs);
		},
		enabled: enabled && canSign && !!authorityStr,
		staleTime: 10_000,
		refetchInterval: (query) => {
			const hasPending = query.state.data?.some((file) => file.isPendingExport);
			return hasPending ? POLL_INTERVAL_MS : false;
		},
	});
}

/**
 * Submits a new export job. Signs the request with the `requestExport` action
 * and invalidates the exports query on success so the new (pending) job appears.
 */
export function useRequestExport() {
	const queryClient = useQueryClient();
	const authority = useVelocityStore(
		(s) => s.wallet.current.adapter?.publicKey
	);
	const authorityStr = authority?.toBase58();
	const { getAuthHeaders } = useApiAuth();
	const setRequestedExportAuthority = useExportsSessionStore(
		(s) => s.setRequestedExportAuthority
	);

	return useMutation({
		mutationFn: async (params: RequestExportParams) => {
			if (!authorityStr) {
				throw new ExportRequestError(401, 'Wallet not connected');
			}

			const headers = await getAuthHeaders(REQUEST_EXPORT_ACTION);

			// Range is inclusive of both UTC calendar days. `from` is the start of
			// the from-day; `to` is the last second of the to-day, which also
			// guarantees `to > from` (the backend rejects equal/zero-length ranges),
			// so a single-day selection still sends a valid non-zero range.
			const from = s3DateStringToUtcSeconds(params.fromS3Date);
			const to =
				s3DateStringToUtcSeconds(params.toS3Date) + SECONDS_PER_DAY - 1;

			const response = await fetch(exportsPath(authorityStr), {
				method: 'POST',
				headers: { ...headers, 'Content-Type': 'application/json' },
				body: JSON.stringify({
					fileType: params.fileType,
					from,
					to,
					userPublicKeys: params.userPublicKeys,
					...(params.marketSymbol ? { market: params.marketSymbol } : {}),
				}),
			});

			if (!response.ok) {
				const errorText = await response.text().catch(() => '');
				throw new ExportRequestError(response.status, errorText);
			}

			return (await response.json()) as RequestExportResponse;
		},
		onSuccess: () => {
			// Enable the app-level background poll so the user gets a "Download
			// ready!" toast even after navigating away from the exports page. Scoped
			// to this authority (guaranteed non-null: the mutation throws above
			// otherwise) so a later wallet switch doesn't inherit the opt-in.
			if (authorityStr) {
				setRequestedExportAuthority(authorityStr);
			}
			queryClient.invalidateQueries({
				queryKey: getExportsQueryKey(authorityStr),
			});
		},
	});
}
